import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  requireAuth?: boolean; // Whether authentication is required (true) or prohibited (false)
}

/**
 * A wrapper component that controls access based on authentication status
 * - When requireAuth=true (default): Redirects unauthenticated users to login
 * - When requireAuth=false: Redirects authenticated users to home
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = "/auth/login",
  requireAuth = true, // Default to requiring authentication
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show nothing while checking authentication
  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  // For protected routes: redirect to login if not authenticated
  if (requireAuth && !isAuthenticated) {
    // Include both pathname and search (query parameters) in the redirect state
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  // For public-only routes: redirect to home if already authenticated
  if (!requireAuth && isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        replace
      />
    );
  }

  // Render children if access conditions are met
  return <>{children}</>;
};

export default ProtectedRoute;
